/**
 * Google Tag Manager + Google Consent Mode v2.
 *
 * - GTM loads ONLY on the standalone landing (never inside partner iframes)
 *   and ONLY after the user grants at least one non-essential cookie category.
 * - Consent Mode: defaults are pushed as "denied" before GTM can load; the
 *   user's choice maps to the four v2 signals (analytics_storage, ad_storage,
 *   ad_user_data, ad_personalization) so Google tags see a proper consent
 *   state and Google Ads does not flag missing consent signals.
 * - Funnel events are pushed to dataLayer unconditionally: they queue in the
 *   array and are processed once (if) GTM loads.
 */

const GTM_ID = "GTM-5XZMDZD9";
const PREFS_KEY = "wyceniarka_cookie_prefs";
const LEGACY_KEY = "wyceniarka_cookie_consent";

export interface CookiePrefs {
  analytics: boolean;
  ads: boolean;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

function dl(): unknown[] {
  window.dataLayer = window.dataLayer ?? [];
  return window.dataLayer;
}

// Google's consent API requires pushing the Arguments object itself (gtag
// style), not a plain array — hence the old-style function.
const gtag = function () {
  // eslint-disable-next-line prefer-rest-params
  dl().push(arguments);
} as unknown as (...args: unknown[]) => void;

/** Must run before GTM could ever load — all consent signals start denied. */
export function setDefaultConsent(): void {
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    wait_for_update: 500,
  });
}

let gtmLoaded = false;

function loadGtm(): void {
  if (gtmLoaded) return;
  gtmLoaded = true;

  dl().push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
}

/** Push a consent update matching the user's choice; load GTM if anything is granted. */
export function applyConsent(prefs: CookiePrefs): void {
  gtag("consent", "update", {
    analytics_storage: prefs.analytics ? "granted" : "denied",
    ad_storage: prefs.ads ? "granted" : "denied",
    ad_user_data: prefs.ads ? "granted" : "denied",
    ad_personalization: prefs.ads ? "granted" : "denied",
  });
  if (prefs.analytics || prefs.ads) {
    loadGtm();
  }
}

export function loadStoredPrefs(): CookiePrefs | null {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return JSON.parse(raw) as CookiePrefs;
    // Migrate the previous accept/decline-only banner choice.
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy === "accepted") return { analytics: true, ads: true };
    if (legacy === "declined") return { analytics: false, ads: false };
    return null;
  } catch {
    return null;
  }
}

export function storePrefs(prefs: CookiePrefs): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // storage unavailable (private mode) — consent still applies for this visit
  }
}

export function pushEvent(event: string, data: Record<string, unknown> = {}): void {
  dl().push({ event, ...data });
}
